import {
    Directive,
    ElementRef,
    Host,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    TemplateRef,
    ViewChild
} from '@angular/core';
import { IProductConfigurableSelection, IProductDataV3 } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { DfEventService } from '@df/core/service/df-event';
import { ElementHeightService } from '@df/dom/element-height/element-height.service';
import { getAbsolutePosition } from '@df/dom/fn/get-absolute-position';
import { WINDOW } from '@df/dom/globals';
import { AbService } from '@df/module-ab/ab.service';
import { GtmService } from '@df/module-gtm/gtm.service';
import { OnChange } from '@df/ng/on-change';
import { BasketService } from '@df/session/api/basket/basket.service';
import { BodyClassService } from '@df/ui/body-class.service';
import { AfterContentInitDirectiveService } from '@df/ui/common/after-content-init.directive';
import { filterMediaByLabel } from '@df/ui/common/filter-media-by-label-pipe';
import { VisibilityService } from '@df/ui/common/visibility.service';
import { NICmsContentComponent } from '@icms/core/content';
import { Nexus } from '@tomandco/nexus-api-types';
import compact from 'lodash-es/compact';
import groupBy from 'lodash-es/groupBy';
import { Subject } from 'rxjs/internal/Subject';
import { Subscription } from 'rxjs/internal/Subscription';
import { AbstractCmsBlockComponent } from '../../../../../cms/block/abstract-cms-block.component';
import { PageHeaderService } from '../../../../../layout/page/header/page-header.service';
import { ProductDecorator } from '../../../../model/product/product.decorator';
import { AddToBasketComponent } from '../../../basket/add-to-basket';
import { ProductMobileFooterComponent } from '../../mobile-basket/product-mobile-footer.component';
import { ProductViewComponent } from '../product-view.component';
import {
    EMediaRowLayout,
    IMediaRow,
    IProductSidebar,
    IProductViewLayoutContent,
    SWATCH_TYPE_OPTIONS,
    TSidebar
} from './product-view-layout.interface';

declare module '@tomandco/nexus-api-types' {
    namespace Nexus.Lib.General {
        interface AppData {
            giftboxesEnabled: boolean;
        }
    }
}

export interface IParentView {
    closeXnotifTray(): void;
    configurableSelection: IProductConfigurableSelection;
    detectChanges$: Subject<void>;
    handleDataChange(data: IProductConfigurableSelection): void;
    isGiftBoxesEnabled: boolean;
    isSidebarOpen: boolean;
    parent?: ProductDecorator<IProductDataV3>;
    product: ProductDecorator<IProductDataV3>;
    productChange?: Subject<ProductDecorator<IProductDataV3>>;
    productView?: ProductViewComponent;
    request: Omit<Nexus.Lib.Basket.BasketRequestData, 'id' | 'qty'>;
    showCountdownBanner: boolean;
    showKlarna: boolean;
    sidebar: TSidebar;
    sidebars: IProductSidebar[];
    xnotifVisible: boolean;
}

@Directive()
export class ProductViewLayoutAbstract
    extends AbstractCmsBlockComponent
    implements IParentView, NICmsContentComponent.IComponent, OnInit, OnChanges, OnDestroy
{
    override content!: IProductViewLayoutContent;
    /**
     * Product getter shorthand
     */
    @Input()
    product!: ProductDecorator<IProductDataV3>;

    @ViewChild('productSizeOptionsModal')
    productSizeOptionsModal!: TemplateRef<any>;

    @ViewChild('mainContent')
    @OnChange(function () {
        (this as ProductViewLayoutAbstract).onMainContentChange();
    })
    mainContent?: ElementRef;

    @ViewChild('afterMainContent')
    @OnChange(function () {
        (this as ProductViewLayoutAbstract).onMainContentChange();
    })
    afterMainContent?: ElementRef;

    @ViewChild('productMediaRef')
    @OnChange('updateGsapOptions')
    productMediaRef?: ElementRef;

    @ViewChild(ProductMobileFooterComponent)
    mobileBasket?: ProductMobileFooterComponent;

    @ViewChild(AddToBasketComponent)
    addToBasketComponent?: AddToBasketComponent;

    configurableSelection = {};

    private basketBottomSubscription?: Subscription;

    /**
     * Parent product shorthand
     */
    get parent() {
        return this.productView.parent;
    }

    /**
     * Request shorthand
     */
    get request() {
        return this.productView.request;
    }

    get isGiftBoxesEnabled() {
        return this.app.data.giftboxesEnabled;
    }

    get cmsTemplate(): string | undefined {
        const cmsTemplate = this.product?.data?.pdp_cms_template;

        if (cmsTemplate) {
            return cmsTemplate.toString();
        }

        return undefined;
    }

    protected labelDiscount = false;

    /**
     * Parent is already detached
     */
    override canDetach = false;

    /**
     * If init unlock is required
     */
    private unlockRequired = true;

    /**
     * Currently viewed sidebar
     */
    sidebar: TSidebar;

    private _isSidebarOpen = false;

    media: Nexus.Lib.Catalog.Type.IProductMediaItem[];
    mediaRows: IMediaRow[][];
    mediaCarouselDetail: Nexus.Lib.Catalog.Type.IProductMediaItem[];

    /**
     * If xnotif is visible
     */
    xnotifVisible = false;

    productChange = new Subject<ProductDecorator<IProductDataV3>>();

    carouselDotsScrollTrigger = {
        id: 'carousel-dots',
        markers: false,
        start: 'bottom +=525px',
        end: 'bottom +=525px',
        toggleClass: 'has-carousel-dots-visible'
    };
    gsapUpdate = new Subject<void>();

    detectChanges$ = new Subject<void>();
    protected abServiceSubscription?: Subscription;

    protected abService = inject(AbService);
    protected elementHeightService = inject(ElementHeightService);
    protected window = inject(WINDOW);
    protected dfEventService = inject(DfEventService);
    protected gtmService = inject(GtmService);

    /**
     * Dependencies
     */
    constructor(
        protected app: App,
        protected afterContentInit: AfterContentInitDirectiveService,
        protected pageHeaderService: PageHeaderService,
        protected visibilityService: VisibilityService,
        protected basketService: BasketService,
        protected bodyClassService: BodyClassService,
        @Host() public productView: ProductViewComponent
    ) {
        super();
    }

    override ngOnInit() {
        super.ngOnInit();

        this.onProductChange();

        this.subscriptions.add(this.app.customer.model.obs.status.subscribe(() => this.detectChanges()));

        this.subscriptions.add(
            this.app.events.on('xnotif-open').subscribe(() => {
                if (this.app.client.isS) {
                    this.openXnotifTray();
                } else {
                    this.openSidebar('xnotif');
                }
            })
        );

        this.subscriptions.add(this.elementHeightService.getState('mobile-footer-content').subscribe(() => this.updateGsapOptions()));

        this.abServiceSubscription = this.abService.getSubject('labelDiscount').subscribe(value => {
            if (value.variant === 'B') {
                this.labelDiscount = true;
            } else {
                this.labelDiscount = false;
            }
            this.detectChanges();
        });

        this.unlock();
    }

    override ngOnChanges(changes: SimpleChanges): void {
        if (changes['product']) {
            this.updateClassNames();
            this.onProductChange();
        }

        super.ngOnChanges(changes);
    }

    override ngOnDestroy() {
        this.abServiceSubscription?.unsubscribe();

        super.ngOnDestroy();

        // reset page header settings
        this.timeoutService.platformSafe(() => this.pageHeaderService.resetHighPriorityState()); // injector may be destroyed

        this.bodyClassService.remove('main-overlay-visible', this);
    }

    get hasSwatchTypeAttributes(): boolean {
        return !!this.parent?.configurableAttributeCodes?.find(i => SWATCH_TYPE_OPTIONS.includes(i));
    }

    get showCountdownBanner(): boolean {
        return this.product.inStock && !!this.product.data?.allow_countdown_baner;
    }

    get nativeElement(): HTMLElement {
        return this.elementRef.nativeElement;
    }

    handleDataChange(data: IProductConfigurableSelection) {
        this.configurableSelection = data;

        this.detectChanges();
    }

    handleInitChange(data: IProductConfigurableSelection) {
        this.productView.matchExactly(data);

        this.handleDataChange(data);
    }

    handleSelectionChange(data: IProductConfigurableSelection) {
        this.productView.matchExactly(data);
    }

    handleSlideChange(): void {
        if (!this.app.isPlatformBrowser) {
            return;
        }

        if (this.window['convert']?.data?.experiments) {
            const experiment = this.abService.getExpById('horizontalImageScroll');
            // check only on first interaction
            if (experiment?.key && !this.window[`recheckGoals${experiment.key}`]) {
                this.window[`recheckGoals${experiment.key}`] = 1;

                this.window['_conv_q'] = this.window['_conv_q'] || [];
                this.window['_conv_q'].push(['recheck_goals']);
                this.app.logger.log('recheck_goals', experiment.key);
            }
        }
    }

    openXnotifEmail() {
        this.app.events.broadcast('xnotif-open');
    }

    onSizeOptionsChange(): void {
        this.addToBasketComponent?.closeTray();
    }

    protected updateGsapOptions() {
        if (!this.app.isPlatformBrowser) {
            return;
        }

        this.timeoutService.setTimeout(() => {
            const bottom = this.productMediaRef?.nativeElement?.getBoundingClientRect().bottom;
            const content = this.elementHeightService.getState('mobile-footer-content').value || 0;
            const height = this.app.client.height;

            const diff = Math.abs(Math.min(0, height - content - bottom));

            this.carouselDotsScrollTrigger.start = `bottom +=${bottom + 20}px`;
            this.carouselDotsScrollTrigger.end = `bottom +=${bottom - diff}px`;

            this.gsapUpdate.next();
        }, 10);
    }

    private onProductChange() {
        this.updateMedia();

        this.productChange.next(this.product);

        this.detectChanges();
    }

    ///////////////// MEDIA //////////////

    protected updateMedia() {
        this.media = this.getMedia();
        this.mediaRows = this.getMediaRows();
        this.mediaCarouselDetail = this.getMediaCarouselDetail();
    }

    /**
     * Retrieves allowed media for gallery
     */
    protected getMedia(): Nexus.Lib.Catalog.Type.IProductMediaItem[] {
        let media = this.product.data?.media || [];

        // filter media if needed
        const [bannedMediaLabels, requiredMediaLabels] = [this.content?.bannedMediaLabels, this.content?.requiredMediaLabels].map(list =>
            list
                ?.split(',')
                .map((i: string) => i.trim().toLowerCase())
                .filter(i => !!i)
        );

        if (requiredMediaLabels?.length) {
            media = filterMediaByLabel(media, requiredMediaLabels, false) || [];
        } else if (bannedMediaLabels?.length) {
            media = filterMediaByLabel(media, bannedMediaLabels, true) || [];
        }

        return media.map(item => ({ ...item, landscape: Array.isArray(item.labels) && item.labels.includes('landscape') }));
    }

    protected getMediaCarouselDetail() {
        let mediaCarouselDetail = this.product.data?.media || [];
        const labels = this.content?.detail_carousel_labels;

        if (labels) {
            const detailCarouselLabels = labels
                .split(',')
                .map((i: string) => i.trim().toLowerCase())
                .filter(i => !!i);

            if (detailCarouselLabels?.length) {
                mediaCarouselDetail = filterMediaByLabel(mediaCarouselDetail, detailCarouselLabels, false);
            }
        }

        return mediaCarouselDetail;
    }

    /**
     * Prepares non-S media rows
     */
    protected getMediaRows(): IMediaRow[][] {
        // group by layout target (landscape labelled go to landscape rows and others go to portrait rows)
        const groupedMedia = groupBy(this.getMedia(), item =>
            item.labels?.includes('landscape') ? EMediaRowLayout.landscape : EMediaRowLayout.portraitsPair
        );

        // distribute items to rows slots
        return (this.content?.galleryRows || [])
            .map(row => {
                const source = groupedMedia[row.layout];
                const qty = row.layout === EMediaRowLayout.landscape || source?.length === 1 ? 1 : 2;

                if (source) {
                    const items = source.splice(0, qty);

                    if (items.length === qty) {
                        return items.map(
                            (item): IMediaRow => this.getMediaRow(item, items, row.layout, row.layout === EMediaRowLayout.landscape)
                        );
                    }
                }

                return undefined;
            })
            .filter(i => !!i);
    }

    protected getMediaRow(item: IMediaRow, items: IMediaRow[], layout: EMediaRowLayout, landscape: boolean): IMediaRow {
        return {
            ...item,
            ratio: landscape ? 'ratio-9-4' : 'ratio-4-5',
            colspan: landscape ? 2 : 1,
            sizes: landscape ? ['100vw', '100vw', '66vw', '66vw'] : ['50vw', '50vw', '33vw', '33vw'],
            uid: [layout, items.map(i => i.image)].join()
        };
    }

    /////////////////// SIDEBAR ////////////////////
    /**
     * Add class to `body` tag.
     */
    protected applyBodyClass(): void {
        this.bodyClassService.set('main-overlay-visible', this, this.xnotifVisible);
    }

    get sidebars(): IProductSidebar[] {
        return compact([
            { title: 'Product Details', sidebar: 'details' },
            { title: 'Delivery & Returns', sidebar: 'delivery' },
            this.showKlarna ? { title: 'Financing - Pay over time', sidebar: 'klarna' } : undefined
        ]);
    }
    /**
     * Open details sidebar
     */

    openSidebar(sidebar: TSidebar) {
        this.sidebar = sidebar;
        this._isSidebarOpen = true;

        this.sendGtmEvent('open' + sidebar.charAt(0).toUpperCase() + sidebar.slice(1));

        this.detectChanges();
    }

    /*
     * Checks if any sidebar is open
     */
    get isSidebarOpen() {
        return this._isSidebarOpen && !!this.sidebar;
    }

    set isSidebarOpen(val: boolean) {
        this._isSidebarOpen = val;

        this.detectChanges();
    }

    get showKlarna(): boolean {
        return this.product.isKlarnaAllowed && this.content.allow_financing_tray;
    }

    openXnotifTray() {
        if (this.client.isS && !this.xnotifVisible) {
            this.xnotifVisible = true;

            this.applyBodyClass();
            this.detectChanges();
        }

        if (this.client.isM && !this.xnotifVisible) {
            this.app.events.broadcast('xnotif-open');
        }
    }

    closeXnotifTray() {
        if (this.xnotifVisible) {
            this.xnotifVisible = false;

            this.applyBodyClass();
            this.detectChanges();
        }
    }

    get sizeGuideVariant(): string | undefined {
        const sizeGuideId = this.productView?.model?.sizeGuideId;

        if (sizeGuideId) {
            return sizeGuideId.toString();
        }

        return undefined;
    }

    /////////////////// HELPERS ////////////////////

    override detectChanges(changes?: NICmsContentComponent.IDetectionChanges) {
        const watchPath = ['content', 'variant', 'image', 'video_id'];

        if (
            watchPath.some(path => (changes?.['content'] as NICmsContentComponent.Mutation)?.changeEvent?.valuePath?.includes(path)) ||
            watchPath.some(path => !!(changes?.['content'] as NICmsContentComponent.Change)?.currentValue?.[path])
        ) {
            this.updateMedia();
        }

        if (this.client.isCms) {
            this.productView.detectChanges();
        }

        super.detectChanges();

        this.detectChanges$.next();

        this.unlock();
    }

    private unlock() {
        if (this.unlockRequired && this.content) {
            this.unlockRequired = false;
            this.timeoutService.setTimeout(() => {
                this.afterContentInit.unlock(this.productView);
            }, 500);
        }
    }

    protected updateClassNames(): void {
        // fallback to old saved data
        const _transparent =
            this.content?.headerTransparent === undefined
                ? (<any>this.content)?.header_transparent
                : this.content?.headerTransparent || false;
        const _lightMode =
            this.content?.headerLightMode === undefined ? (<any>this.content)?.header_light_mode : this.content?.headerLightMode || false;

        const transparent = _transparent === undefined ? true : !!_transparent;
        const lightMode = transparent && !!_lightMode;

        this.pageHeaderService.setHighPriorityState({
            transparent,
            lightMode,
            hideUp: false
        });
    }

    /**
     * Quickly written method to make sure basket hides when we reach the bottom of the PP
     */
    private onMainContentChange() {
        this.basketBottomSubscription?.unsubscribe();

        let mainContentVisible = true;
        let afterMainContentVisible = false;

        const update = () => {
            this.mobileBasket?.toggle(mainContentVisible && !afterMainContentVisible);
        };

        if (this.mainContent && this.afterMainContent) {
            this.basketBottomSubscription = new Subscription();

            this.basketBottomSubscription.add(
                this.visibilityService.isInView(this.mainContent).subscribe(visible => {
                    mainContentVisible = visible;
                    update();
                })
            );

            this.basketBottomSubscription.add(
                this.visibilityService.isInView(this.afterMainContent).subscribe(visible => {
                    const afterMainContentPosition = getAbsolutePosition(this.afterMainContent?.nativeElement);

                    if (this.client.height > afterMainContentPosition.y) {
                        afterMainContentVisible = false;
                    } else {
                        afterMainContentVisible = visible;
                    }

                    update();
                })
            );
        } else {
            mainContentVisible = true;
            afterMainContentVisible = false;

            update();
        }
    }

    private sendGtmEvent(event: string): void {
        this.gtmService.push({ event });
    }
}
